/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import * as Utils from '@contentstack/utils'
import {Stack} from 'contentstack'

type GetEntry = {
    stack: Stack
    contentTypeUid: string
    locale: string
    referenceFieldPath: string[] | undefined
    jsonRtePath: string[] | undefined
}

type GetEntryByUid = {
    stack: Stack
    contentTypeUid: string
    entryUid: string
}

type GetEntryByUrl = {
    stack: Stack
    entryUrl: string | undefined
    locale: string
    contentTypeUid: string
    referenceFieldPath: string[] | undefined
    jsonRtePath: string[] | undefined
}

type GetEntryByPspUrl = {
    stack: Stack
    url: string
    locale: string
    contentTypeUid: string
}

type GetEntryByTitle = {
    stack: Stack
    title: string | undefined
    locale: string
    contentTypeUid: string
    referenceFieldPath: string[] | undefined
    jsonRtePath: string[] | undefined
}

type GetEntryByCategorysfcc = {
    stack: Stack
    categoryId: string
    locale: string
    contentTypeUid: string
    referenceFieldPath: string[] | undefined
    jsonRtePath: string[] | undefined
}

type GetEntryByProductsfcc = {
    stack: Stack
    productId: string
    locale: string
    contentTypeUid: string
    referenceFieldPath: string[] | undefined
    jsonRtePath: string[] | undefined
}

const renderOption = {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any
    span: (node: any, next: any) => next(node.children),
}

/**
 *
 * fetches all the entries from specific content-type
 * @param {* content-type uid} contentTypeUid
 * @param {* reference field name} referenceFieldPath
 * @param {* Json RTE path} jsonRtePath
 *
 */
export const getEntry = ({
    stack,
    contentTypeUid,
    referenceFieldPath,
    jsonRtePath,
    locale,
}: GetEntry) => {
    return new Promise((resolve, reject) => {
        const query = stack.ContentType(contentTypeUid).Query()
        if (referenceFieldPath) query.includeReference(referenceFieldPath)
        if (locale) query.includeFallback().language(locale)
        query
            .addQuery('include_all', 'true')
            .addQuery('include_all_depth', '5')
            .toJSON()
            .find()
            .then(
                (result) => {
                    jsonRtePath &&
                        Utils.jsonToHTML({
                            entry: result,
                            paths: jsonRtePath,
                            renderOption,
                        })
                    resolve(result[0])
                },
                (error) => {
                    reject(error)
                },
            )
    })
}

/**
 *
 * fetches all the entries from specific content-type
 * @param {* content-type uid} contentTypeUid
 * @param {* reference field name} referenceFieldPath
 * @param {* Json RTE path} jsonRtePath
 *
 */
export const getEntryByUid = ({stack, contentTypeUid, entryUid}: GetEntryByUid) => {
    return new Promise((resolve, reject) => {
        const query = stack.ContentType(contentTypeUid).Entry(entryUid)
        query
            .addQuery('include_all', 'true')
            .addQuery('include_all_depth', '5')
            .toJSON()
            .fetch()
            .then(
                (result) => {
                    resolve(result)
                },
                (error) => {
                    reject(error)
                },
            )
    })
}

/**
 *fetches specific entry from a content-type
 *
 * @param {* content-type uid} contentTypeUid
 * @param {* url for entry to be fetched} entryUrl
 * @param {* reference field name} referenceFieldPath
 * @param {* Json RTE path} jsonRtePath
 * @returns
 */
export const getEntryByUrl = ({
    stack,
    contentTypeUid,
    entryUrl,
    referenceFieldPath,
    jsonRtePath,
    locale,
}: GetEntryByUrl) => {
    return new Promise((resolve, reject) => {
        const query = stack.ContentType(contentTypeUid).Query()
        if (referenceFieldPath) query.includeReference(referenceFieldPath)
        if (locale) query.includeFallback().language(locale)
        query.addQuery('include_all', 'true').addQuery('include_all_depth', '5').toJSON()
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const data = query.where('url', `${entryUrl}`).find()
        data.then(
            (result) => {
                jsonRtePath &&
                    Utils.jsonToHTML({
                        entry: result,
                        paths: jsonRtePath,
                        renderOption,
                    })
                resolve(result[0])
            },
            (error) => {
                console.error(error)
                reject(error)
            },
        )
    })
}

/**
 *fetches specific entry from a content-type
 *
 * @param {* content-type uid} contentTypeUid
 * @param {* title for entry to be fetched} entryUrl
 * @param {* reference field name} referenceFieldPath
 * @param {* Json RTE path} jsonRtePath
 * @returns
 */
export const getEntryByTitle = ({
    stack,
    contentTypeUid,
    title,
    referenceFieldPath,
    jsonRtePath,
    locale = 'en',
}: GetEntryByTitle) => {
    return new Promise((resolve, reject) => {
        const blogQuery = stack.ContentType(contentTypeUid).Query()
        if (referenceFieldPath) blogQuery.includeReference(referenceFieldPath)

        blogQuery.toJSON()

        const data = blogQuery
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            .where('title', `${title}`)
            .includeFallback()
            .language(locale)
            .find()

        data.then(
            (result) => {
                jsonRtePath &&
                    Utils.jsonToHTML({
                        entry: result,
                        paths: jsonRtePath,
                        renderOption,
                    })
                resolve(result[0])
            },
            (error) => {
                console.error(error)
                reject(error)
            },
        )
    })
}

/**
 *
 * fetches all the entries from specific content-type
 * @param {* content-type uid} contentTypeUid
 * @param {* reference field name} referenceFieldPath
 * @param {* Json RTE path} jsonRtePath
 *
 */
export const getEntryByCategorysfcc = ({
    stack,
    categoryId,
    contentTypeUid,
    jsonRtePath,
    locale,
}: GetEntryByCategorysfcc) => {
    return new Promise((resolve, reject) => {
        const query = stack.ContentType(contentTypeUid).Query()
        query
            .where('categorysfcc.data.id', categoryId)
            .includeFallback()
            .language(locale)
            .addQuery('include_all', 'true')
            .addQuery('include_all_depth', '5')
            .toJSON()
            .find()
            .then(
                (result) => {
                    jsonRtePath &&
                        Utils.jsonToHTML({
                            entry: result,
                            paths: jsonRtePath,
                            renderOption,
                        })
                    resolve(result[0])
                },
                (error) => {
                    reject(error)
                },
            )
    })
}

export const getPageByPspUrl = ({stack, contentTypeUid, url, locale}: GetEntryByPspUrl) => {
    return new Promise((resolve, reject) => {
        // is the same as
        // {"psp_url":{"$in_query":{"url":"test"}}}
        const pspUrlQuery = stack.ContentType('psp_url').Query().where('url', url)
        const query = stack.ContentType(contentTypeUid).Query()
        query
            .addQuery('include_all', 'true')
            .addQuery('include_all_depth', '5')
            .includeFallback()
            .language(locale)
            .referenceIn('psp_url', pspUrlQuery)
            .toJSON()
            .find()
            .then(
                (result) => {
                    resolve(result[0])
                },
                (error) => {
                    reject(error)
                },
            )
    })
}

/**
 *
 * fetches all the entries from specific content-type
 * @param {* content-type uid} contentTypeUid
 * @param {* reference field name} referenceFieldPath
 * @param {* Json RTE path} jsonRtePath
 *
 */
export const getEntryByProductsfcc = ({
    stack,
    productId,
    contentTypeUid,
    jsonRtePath,
    locale,
}: GetEntryByProductsfcc) => {
    return new Promise((resolve, reject) => {
        const query = stack.ContentType(contentTypeUid).Query()
        query
            .where('productsfcc.data.id', productId)
            .includeFallback()
            .language(locale)
            .addQuery('include_all', 'true')
            .addQuery('include_all_depth', '5')
            .toJSON()
            .find()
            .then(
                (result) => {
                    jsonRtePath &&
                        Utils.jsonToHTML({
                            entry: result,
                            paths: jsonRtePath,
                            renderOption,
                        })
                    resolve(result[0])
                },
                (error) => {
                    reject(error)
                },
            )
    })
}
